import { makeStyles } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useSearchManufacturerColorsLazyQuery } from '@paintscout/api'
import { jsonToLucene } from 'json-to-lucene'
import Typography from '@paintscout/ui/components/Typography'
import DropdownSelect from '../DropdownSelect'
import type { DropdownSelectOption, SingleSelectProps } from '../DropdownSelect'

export interface ManufacturerColorPickerProps extends Omit<SingleSelectProps, 'variant'> {
  manufacturer?: string
}

const useStyles = makeStyles({
  swatch: {
    display: 'inline-block',
    width: 16,
    height: 16,
    borderRadius: 2,
    marginRight: 8,
    border: '1px solid #ccc',
    flexShrink: 0
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center'
  },
  error: {
    '& a': {
      color: 'inherit'
    }
  }
})

export function ManufacturerColorPicker({ manufacturer, ...props }: ManufacturerColorPickerProps) {
  const classes = useStyles({})
  const [options, setOptions] = useState<DropdownSelectOption[]>([])
  const limit = 20

  const handleQuery = (queryText: string) =>
    jsonToLucene({
      query: queryText,
      filters: manufacturer ? [{ id: 'manufacturer', type: 'equal', value: manufacturer }] : undefined
    }) || '*'

  const [searchColors, { loading, refetch, error }] = useSearchManufacturerColorsLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const rows = data?.searchManufacturerColors?.rows ?? []
      setOptions(
        rows.map((color) => ({
          label: `${color.number} - ${color.name}`,
          value: `${color.number} - ${color.name}`,
          dotColor: color.hexColor
        }))
      )
    }
  })

  useEffect(() => {
    searchColors({
      variables: {
        query: handleQuery(''),
        sort: ['intRank', 'name.keyword'],
        limit
      }
    })
  }, [])

  const handleSearchCallback = (queryText: string) => {
    searchColors({
      variables: {
        query: handleQuery(queryText),
        sort: ['intRank', 'name.keyword'],
        limit
      }
    })
  }

  return (
    <div>
      <DropdownSelect
        {...props}
        label={'Color'}
        searchable
        variant={'single'}
        disabled={props.disabled || !!error}
        loading={loading}
        options={options}
        placeholder={{ selectPlaceholder: 'Color', plural: 'colors' }}
        searchCallback={handleSearchCallback}
        createAtTop={true}
      />
      {error && (
        <div className={classes.error}>
          <Typography variant="subtitle2">
            Unable to load colors.{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                refetch()
              }}
            >
              Retry?
            </a>
          </Typography>
        </div>
      )}
    </div>
  )
}

export default ManufacturerColorPicker
